import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, BookOpen, Loader2 } from 'lucide-react';
import SEO from '../components/seo/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAG_LABELS = {
  'deadlines': 'Deadlines',
  'underperformance': 'Underperformance',
  'stakeholders': 'Stakeholders',
  'new-manager': 'New Manager',
  '1-1s': '1:1s',
  'difficult-conversations': 'Difficult Conversations',
  'feedback': 'Feedback',
  'priorities': 'Priorities',
  'team-health': 'Team Health',
};

const labelForTag = (t) => TAG_LABELS[t] || t;

const SituationsHubPage = () => {
  const [data, setData] = useState({ situations: [], tags: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeTag, setActiveTag] = useState(null);
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query.trim()) params.set('q', query.trim());
        if (activeTag) params.set('tag', activeTag);
        const res = await fetch(`${API_URL}/api/situations-hub?${params.toString()}`);
        if (res.ok) setData(await res.json());
      } catch { /* swallow */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [query, activeTag]);

  useEffect(() => {
    fetch(`${API_URL}/api/situations-hub`)
      .then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d) => setAllTags(d.tags || []))
      .catch(() => {});
  }, []);

  const tagChips = useMemo(() => allTags, [allTags]);

  return (
    <div className="min-h-screen bg-[#f5f5f0]" data-testid="situations-hub-page">
      <SEO
        title="Situations Hub — Real IT leadership answers"
        description="Concise, practical answers to the situations IT managers actually face — missed deadlines, difficult conversations, new-manager challenges and more."
      />
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <Link to="/" className="text-red-600 hover:text-red-700 text-sm">← Back to 30 Day Skills</Link>
          <div className="flex items-start gap-4 mt-6">
            <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-red-100 items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-3 leading-tight">Situations Hub</h1>
              <p className="text-stone-600 text-lg leading-relaxed max-w-2xl">
                Concise, practical answers to the situations IT managers actually face.
                No theory. No fluff. The answer you needed last Tuesday at 3pm.
              </p>
              <p className="text-stone-500 text-sm mt-4">
                {data.total > 0 ? `${data.total} situations` : 'Loading…'} · Updated regularly
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search e.g. 'missed deadline', 'former peer', 'underperforming'…"
              data-testid="situations-search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-stone-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none text-stone-900 placeholder:text-stone-400" />
          </div>
          {tagChips.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="situations-tag-chips">
              <button type="button" onClick={() => setActiveTag(null)} data-testid="tag-chip-all"
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeTag === null ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}>All</button>
              {tagChips.map((t) => (
                <button key={t.tag} type="button" onClick={() => setActiveTag(activeTag === t.tag ? null : t.tag)}
                  data-testid={`tag-chip-${t.tag}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${activeTag === t.tag ? 'bg-red-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}>
                  {labelForTag(t.tag)} <span className="opacity-60">· {t.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 text-stone-400 animate-spin inline-block" /></div>
        ) : data.situations.length === 0 ? (
          <div className="text-center py-16 text-stone-500" data-testid="situations-empty">
            No situations match your search. Try different keywords or clear the filter.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="situations-grid">
            {data.situations.map((s) => (
              <Link key={s.slug} to={`/situations/${s.slug}`} data-testid={`situation-card-${s.slug}`}
                className="group block bg-white border border-stone-200 hover:border-red-400 hover:shadow-md transition-all rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-stone-900 group-hover:text-red-700 mb-2 leading-snug">{s.title}</h2>
                {s.summary && <p className="text-stone-600 text-sm leading-relaxed mb-4 line-clamp-3">{s.summary}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    {s.tags.slice(0, 3).map((t) => (
                      <span key={t} className="px-2 py-0.5 text-[11px] rounded-full bg-stone-100 text-stone-600">{labelForTag(t)}</span>
                    ))}
                  </div>
                  <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-red-600 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 p-8 bg-stone-900 rounded-2xl text-center">
          <h3 className="text-2xl font-semibold text-white mb-2">Need help with a situation that isn&apos;t listed?</h3>
          <p className="text-stone-400 mb-5 max-w-xl mx-auto">
            Bring your specific case to the AI Companion. It remembers context, helps you think it through, and builds a record of how you handled it.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/companion" data-testid="situations-hub-companion-cta"
              className="px-5 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700">Try the Companion</Link>
            <Link to="/it-leadership" className="px-5 py-2.5 rounded-lg border border-stone-700 text-stone-200 font-medium hover:bg-stone-800">Browse the book series</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SituationsHubPage;
